import { PackageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { useQuery } from '@tanstack/react-query';
import { apiOrdersGetCollection } from '@r/api/generated/client.ts';
import type {
  ApiOrdersGetCollectionParams,
  OrderOrderCollectionReadModel,
  OrderProductOrderCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrderColumns, useFilterFields } from '@r/components/home/order-columns.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import type { ColumnFiltersState, ColumnSort, PaginationState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { unparse } from 'papaparse';
import { format } from 'date-fns';
import { downloadCSV } from '@r/utils/utils.ts';
import { fr } from 'date-fns/locale';
import { buildApiDateRangeParams } from '@r/utils/date-range-filter.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type OrderCollection = OrderOrderCollectionReadModel;
type OrderProduct = OrderProductOrderCollectionReadModel;
type Sort = 'asc' | 'desc' | undefined;

function parsePositiveAmountFilterValue(value: string): number | undefined {
  if (!value.trim()) return undefined;

  const normalizedValue = value.replace(',', '.').trim();
  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) return undefined;

  return Math.abs(parsedValue);
}

type OrderFilters = {
  reference: string;
  companyName: string;
  companyUniqueCode: string;
  madeByUniqueCode: string;
  madeByFullname: string;
  madeByEmail: string;
  shopName: string;
  productName: string;
  productId: string;
  productsCountMin: string;
  productsCountMax: string;
  pointAmountMin: string;
  pointAmountMax: string;
  createdAt: string;
  status: OrderCollection['status'][] | undefined;
  createdAtSortValue: Sort;
  pointAmountSortValue: Sort;
  shopNameSortValue: Sort;
  productsCountSortValue: Sort;
  statusSortValue: Sort;
};

export const Orders = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const authUser = useUser();
  const user = authUser.data;
  const workspaces = UseWorkspace(user);
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const [isExporting, setIsExporting] = useState(false);

  const filterFields = useFilterFields({ admin: true });
  const initialOrders: OrderCollection[] = [];
  const [data, setData] = useState<OrderCollection[]>(initialOrders);
  const [totalPages, setTotalPages] = useState(1);

  const ordersTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('orders', authUser.data?.id),
    [authUser.data?.id]
  );

  async function ordersListFiltersQuery(
    filters: OrderFilters,
    page: number | null = null,
    limit: number | null = null
  ) {
    const {
      reference,
      companyName,
      companyUniqueCode,
      madeByUniqueCode,
      madeByFullname,
      madeByEmail,
      shopName,
      productName,
      productId,
      productsCountMin,
      productsCountMax,
      pointAmountMin,
      pointAmountMax,
      createdAt,
      status,
      createdAtSortValue,
      pointAmountSortValue,
      shopNameSortValue,
      productsCountSortValue,
      statusSortValue
    } = filters;

    try {
      const parsedProductId = Number(productId);
      const hasValidProductId = Number.isFinite(parsedProductId) && parsedProductId > 0;
      const parsedPointAmountMin = parsePositiveAmountFilterValue(pointAmountMin);
      const parsedPointAmountMax = parsePositiveAmountFilterValue(pointAmountMax);
      // API values are negative for expenses:
      // - user min maps to lte (-min)
      // - user max maps to gte (-max)
      const apiPointAmountGte =
        parsedPointAmountMax !== undefined ? String(-parsedPointAmountMax) : undefined;
      const apiPointAmountLte =
        parsedPointAmountMin !== undefined ? String(-parsedPointAmountMin) : undefined;

      const params: ApiOrdersGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(reference && { reference }),
        ...(companyName && { 'company.name': companyName }),
        ...(companyUniqueCode && { 'company.uniqueCode': companyUniqueCode }),
        ...(madeByUniqueCode && { 'madeBy.uniqueCode': madeByUniqueCode }),
        ...(madeByFullname && { 'madeBy.user.fullname': madeByFullname }),
        ...(madeByEmail && { 'madeBy.user.email': madeByEmail }),
        ...(shopName && { 'orderProducts.product.shop.displayName': shopName }),
        ...(productName && { 'orderProducts.product.name': productName }),
        ...(hasValidProductId ? { 'orderProducts.product.id': parsedProductId } : {}),
        ...(productsCountMin && { 'productsCount[gte]': productsCountMin }),
        ...(productsCountMax && { 'productsCount[lte]': productsCountMax }),
        ...(apiPointAmountGte && { 'point.amount[gte]': apiPointAmountGte }),
        ...(apiPointAmountLte && { 'point.amount[lte]': apiPointAmountLte }),
        ...buildApiDateRangeParams('createdAt', createdAt),
        ...(status && { 'status[]': status }),
        ...(createdAtSortValue && { 'order[createdAt]': createdAtSortValue }),
        ...(pointAmountSortValue && { 'order[point.amount]': pointAmountSortValue }),
        ...(shopNameSortValue && {
          'order[orderProducts.product.shop.displayName]': shopNameSortValue
        }),
        ...(productsCountSortValue && { 'order[productsCount]': productsCountSortValue }),
        ...(statusSortValue && { 'order[status]': statusSortValue })
      };

      return await apiOrdersGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/orders')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  function ordersListFilters(): OrderFilters {
    const reference =
      (columnFilters.find((filter) => filter.id === 'reference')?.value as string) ?? '';
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const companyUniqueCode =
      (columnFilters.find((filter) => filter.id === 'companyUniqueCode')?.value as string) ?? '';
    const madeByUniqueCode =
      (columnFilters.find((filter) => filter.id === 'madeByUniqueCode')?.value as string) ?? '';
    const madeByFullname =
      (columnFilters.find((filter) => filter.id === 'madeByFullname')?.value as string) ?? '';
    const madeByEmail =
      (columnFilters.find((filter) => filter.id === 'madeByEmail')?.value as string) ?? '';
    const shopName =
      (columnFilters.find((filter) => filter.id === 'shopName')?.value as string) ?? '';
    const productName =
      (columnFilters.find((filter) => filter.id === 'productName')?.value as string) ?? '';
    const productId =
      (columnFilters.find((filter) => filter.id === 'productId')?.value as string) ?? '';
    const productsCountMin =
      (columnFilters.find((filter) => filter.id === 'productsCountMin')?.value as string) ?? '';
    const productsCountMax =
      (columnFilters.find((filter) => filter.id === 'productsCountMax')?.value as string) ?? '';
    const pointAmountMin =
      (columnFilters.find((filter) => filter.id === 'pointAmountMin')?.value as string) ?? '';
    const pointAmountMax =
      (columnFilters.find((filter) => filter.id === 'pointAmountMax')?.value as string) ?? '';
    const createdAt =
      (columnFilters.find((filter) => filter.id === 'createdAt')?.value as string) ?? '';

    const statusFilter = columnFilters.find((filter: { id: string }) => filter.id === 'status');
    const status = statusFilter?.value as OrderCollection['status'][] | undefined;

    const sortValue = (id: string): Sort => {
      const sort = sorting.find((s: ColumnSort) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    const createdAtSortValue = sortValue('createdAt');

    const pointAmountSort = sorting.find((sort: ColumnSort) => sort.id === 'pointAmount');
    const pointAmountSortValue: Sort = pointAmountSort
      ? pointAmountSort.desc
        ? 'asc'
        : 'desc'
      : undefined;

    return {
      reference,
      companyName,
      companyUniqueCode,
      madeByUniqueCode,
      madeByFullname,
      madeByEmail,
      shopName,
      productName,
      productId,
      productsCountMin,
      productsCountMax,
      pointAmountMin,
      pointAmountMax,
      createdAt,
      status,
      createdAtSortValue,
      pointAmountSortValue,
      shopNameSortValue: sortValue('shopName'),
      productsCountSortValue: sortValue('productsCount'),
      statusSortValue: sortValue('status')
    };
  }

  function getProductDataForExport(order: OrderCollection, orderProduct: OrderProduct) {
    const sanitize = (value: string | undefined) => value?.replace(/[\r\n\t]/g, '') ?? '';

    return [
      order.reference,
      format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: fr }),
      order.shops[0]?.displayName ?? '',
      orderProduct.product.id,
      orderProduct.product.name,
      orderProduct.quantity,
      orderProduct.pointUnitPrice,
      orderProduct.quantity * orderProduct.pointUnitPrice,
      t(order.status),
      order.company?.uniqueCode,
      sanitize(order.company?.name),
      order.madeBy?.uniqueCode,
      sanitize(order.madeBy?.user?.fullname),
      sanitize(order.madeBy?.user?.email),
      order.madeBy?.phone
    ];
  }

  const { table } = useDataTable({
    data,
    columns: useOrderColumns({ admin: true }),
    filterFields,
    pageCount: totalPages,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: ordersTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;

  const {
    data: ordersPaginatedQuery,
    isPending: isOrdersPending,
    isError: isOrdersError,
    isSuccess: isOrdersSuccess
  } = useQuery({
    queryKey: [
      'order',
      workspaces.workspace?.id,
      user?.id,
      pageIndex,
      pageSize,
      columnFilters,
      sorting
    ],
    queryFn: async () => {
      const orders = await ordersListFiltersQuery(ordersListFilters());

      for (let i = 0; i < orders.data.length; i++) {
        (orders.data[i] as OrderCollection & { url: string }).url =
          `/shops/orders/${orders.data[i].id}`;
      }
      return orders;
    },
    enabled: !!user && !!workspaces.workspace?.id,
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    if (ordersPaginatedQuery) {
      setData(ordersPaginatedQuery.data);
      setTotalPages(ordersPaginatedQuery.pagination?.last ?? 1);
    }
  }, [ordersPaginatedQuery]);

  async function exportData() {
    setIsExporting(true);

    let allData: OrderCollection[] = [];
    const limit = 500;

    // Récupérer le nombre total de pages
    const totalPageCount = Math.ceil((ordersPaginatedQuery?.pagination?.totalItems ?? 1) / limit);

    // Créer un tableau de promesses pour chaque page de données
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      ordersListFiltersQuery(ordersListFilters(), i + 1, limit)
    );

    // Attendre que toutes les promesses soient résolues
    const pagesData = await Promise.all(promises);

    // Utiliser flatMap pour transformer et aplatir les données de chaque page en un seul tableau
    allData = pagesData.flatMap((pageData) => pageData.data);

    // Définir les noms des colonnes pour l'en-tête du CSV
    const fields = [
      t('label.order.reference'),
      t('label.order.created.at'),
      t('label.order.shop.name'),

      t('order.product.id'),
      t('order.item'),
      t('order.quantity'),
      t('global.unit.price'),
      t('global.total.amount'),

      t('label.order.status'),
      t('export.company.unique.code'),
      t('label.order.company.name'),

      t('export.user.unique.code'),
      t('label.user.fullname'),
      t('label.user.email'),
      t('label.user.phone')
    ];

    // Extraire les données des commandes et des produits pour le CSV
    const ordersData = allData.flatMap(
      (order) =>
        order.orderProducts?.map((orderProduct) => getProductDataForExport(order, orderProduct)) ??
        []
    );

    // Générer le contenu du CSV
    const csv = unparse({
      fields: fields,
      data: ordersData
    });

    const currentDate = new Date();

    // Générer le nom du fichier CSV
    const baseFileName = `orders-export-${format(currentDate, 'yyyy-MM-dd-HHmmss')}.csv`;

    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;

    downloadCSV(csv, fileName);
    setIsExporting(false);
  }

  return (
    <>
      <div className="space-y-2.5">
        <h2 className="text-4xl font-bold">{t('admin.order.tracking.title')}</h2>
        <p className="font-light text-muted-foreground">{t('admin.order.tracking.description')}</p>
      </div>

      {(isOrdersPending || isOrdersError) && (
        <DataTableSkeleton
          columnCount={10}
          cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
        />
      )}

      {isOrdersSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />

          <DataTable
            table={table}
            totalItems={ordersPaginatedQuery?.pagination?.totalItems}
            emptyIcon={PackageIcon}
            emptyMessageKey="admin.order.empty"
            emptyFilteredMessageKey="order.filter.empty"
          />
        </>
      )}
    </>
  );
};
